import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CacheService } from './cache.service';
import { NotificationMessage } from '../services/model/notification-message.model';
import { DialogType } from '../services/model/dialog-type.enum';
import { ExecutingTaskMonitorService } from './executing-task-monitor.service';

const PAYMENT_DECLINED =
  'Your payment was declined. Please try a different payment method.';
const PAYMENT_RETRY =
  'Payment could not be completed. Please retry later.';

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandlerService implements ErrorHandler {
  constructor(
    private readonly _injector: Injector,
    private readonly _cache: CacheService,
    private readonly _executingTaskSvc: ExecutingTaskMonitorService,
  ) {}

  public handleError(error: Error | HttpErrorResponse): void {
    console.error('handleError', error);

    const errorMessage: string = 'An error has occurred; Please try again.';
    let errorDetail: string = '';

    if (error instanceof HttpErrorResponse) {
      // server side error
      let eventId = (error as any)?.error?.cdiEventId;
      if (error.status != null && error.status != undefined) {
        if (error.status === 0) {
          errorDetail = `System Not Available`;
        } else {
          if (error.error && error.status === 401) {
            errorDetail = '401. Invalid Payment Link.';
          } else if (error.error && error.status === 403) {
            errorDetail =
              '403. Authorization Failed. You do not have permissions to access resource.';
          } else if (error.error && error.status === 400) {
            eventId = this._cache.generateGuid();
            errorDetail = `${this.parseBadRequest(error)}`;
          } else if (error.error && error.status === 404) {
            errorDetail = `Status Code: 404; Not Found`;
          } else if (error.error && error.status === 405) {
            errorDetail = `Status Code: 405; Method Not Allowed`;
          } else if (error.error && error.status === 429) {
            console.error('429', error);
          } else if (error.error && error.status === 500) {
            errorDetail = `${this.parseErrorMessage(error)}`;
          } else if (error.status === 530) {
            errorDetail = this.parsePaymentFailure(error);
          }
        }
        this._executingTaskSvc.showFullPageSpinner.next(false);
        this.showErrorMessage([errorDetail], eventId);
      }
    } else {
      // client side error
      this.showErrorMessage([error.message], 'Cli');
    }
  }

  private parsePaymentFailure(err: HttpErrorResponse): string {
    const response = err?.error?.response;
    if (response === PAYMENT_DECLINED) {
      return PAYMENT_DECLINED;
    }
    return PAYMENT_RETRY;
  }

  private showErrorMessage(message: string[], cdiEventId: string) {
    this._cache.showDialog.next(
      new NotificationMessage(DialogType.error, 'Error', message, cdiEventId),
    );
  }

  private custom500Error(text: any, errorNumber: number): string {
    let message: string = '';
    try {
      message = `${text.message}`;
    } catch (error) {
      console.error('custom500Error', error);
    }
    return message;
  }

  private parseErrorMessage(err: HttpErrorResponse): string {
    let message: string = '';
    try {
      if (err.error != null && err.error != undefined) {
        if (
          (err.error.cdiEventId === null ||
            err.error.cdiEventId === undefined) &&
          (err.error.message === null || err.error.message === undefined)
        ) {
          message = `${err.error}`;
        } else {
          message = `${err.error.message}`;
        }
      }
    } catch (error) {
      console.error('parseErrorMessage', error);
      return 'unavailable.';
    }
    return message;
  }

  private parseBadRequest(err: any): string {
    let errorMessage: string = 'Validation Error';
    try {
      if (err.error != null && err.error != undefined) {
        if (
          err?.error?.title?.includes('One or more validation errors occurred')
        ) {
          errorMessage = `One or more validation errors occurred.\n ${this.parseValidationError(
            err.error.errors,
          )}`;
        }
      }
    } catch (error) {
      console.error('parseBadRequest', error);
      return 'unavailable.';
    }
    return errorMessage;
  }

  private parseValidationError(data: any): string {
    let validationErrors: string = '';
    try {
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          validationErrors = validationErrors + `${data[key]}\n`;
        }
      }
    } catch (error) {
      console.error('parseValidationError', error);
      return 'Unable to Parse Validation Error';
    }
    return validationErrors;
  }
}
